import React, { useCallback, useEffect, useMemo, useState } from "react"
import { Box, Button, CircularProgress, Typography } from "@mui/material"
import MUIDataTable from "mui-datatables"
import { collection, doc, getDoc, getDocs } from "firebase/firestore"
import { DateTime } from "luxon"
import { useNavigate } from "react-router-dom"

import { db } from "../../firebase"
import { PopupService } from "react-ui-components/components/PopupService"
import { hasRaffleExpired, isActiveTicketBatchRow, RaffleExpiryFields } from "../TicketsTableUtils"

type RaffleUserRow = {
  id: string
  name?: string
  email?: string
  phoneNumber?: string
  ticketsCount: number
  tickets: any[]
}

const getTicketOwnerId = (ticket: any) =>
  ticket?.payerUID || ticket?.userUID || ticket?.userId || ticket?.uid

const formatExpirationDate = (value: any) => {
  if (!value) {
    return "N/A"
  }

  if (typeof value === "number") {
    return DateTime.fromMillis(value).toLocaleString(DateTime.DATE_SHORT)
  }

  const parsed = DateTime.fromISO(value)
  return parsed.isValid ? parsed.toLocaleString(DateTime.DATE_SHORT) : String(value)
}

const getExpirationMillis = (value: any) => {
  if (typeof value === "number") {
    return value
  }

  const parsed = DateTime.fromISO(value)
  return parsed.isValid ? parsed.toMillis() : 0
}

// A raffle ending (drawn, or disabled by an admin) doesn't retroactively touch tickets already
// issued for it, so this page otherwise keeps listing/counting them for a raffle that's already
// over (#207). Mirrors the same raffle-aware filter already applied to the Tickets tab
// (TicketsTableUtils.normalizeTicketBatchList).
export const filterActiveRaffleTickets = (
  tickets: Record<string, any>[],
  raffle?: RaffleExpiryFields | null,
  now: number = Date.now(),
): Record<string, any>[] => {
  if (hasRaffleExpired(raffle, now)) {
    return []
  }

  return tickets.filter((ticket) => isActiveTicketBatchRow(ticket, now))
}

const RaffleUsers = ({
  raffleId,
  raffle,
}: {
  raffleId?: string
  raffle?: RaffleExpiryFields | null
}) => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [users, setUsers] = useState<RaffleUserRow[]>([])
  const [stats, setStats] = useState({ users: 0, tickets: 0 })
  const navigate = useNavigate()

  const fetchUsers = useCallback(async () => {
    if (!raffleId) {
      setUsers([])
      setStats({ users: 0, tickets: 0 })
      return
    }

    setLoading(true)
    setError(null)

    try {
      const ticketsSnapshot = await getDocs(collection(db, `raffles/${raffleId}/tickets`))
      const tickets = ticketsSnapshot.docs.map((ticketDoc) => ({
        id: ticketDoc.id,
        ...ticketDoc.data(),
      }))
      const activeTickets = filterActiveRaffleTickets(tickets, raffle)

      const ticketsByUser: Record<string, any[]> = {}
      activeTickets.forEach((ticket) => {
        const userId = getTicketOwnerId(ticket)
        if (!userId) {
          return
        }

        if (!ticketsByUser[userId]) {
          ticketsByUser[userId] = []
        }
        ticketsByUser[userId].push(ticket)
      })

      const userIds = Object.keys(ticketsByUser)
      if (!userIds.length) {
        setUsers([])
        setStats({ users: 0, tickets: 0 })
        return
      }

      const userRows = await Promise.all(
        userIds.map(async (userId) => {
          const userSnap = await getDoc(doc(db, "users", userId))
          const userData = userSnap.exists() ? userSnap.data() : {}

          return {
            id: userId,
            name: userData?.name || userData?.displayName || "Unknown",
            email: userData?.email || "",
            phoneNumber: userData?.phoneNumber || "",
            ticketsCount: ticketsByUser[userId].length,
            tickets: ticketsByUser[userId],
          } as RaffleUserRow
        }),
      )

      userRows.sort((a, b) => b.ticketsCount - a.ticketsCount)
      setUsers(userRows)
      setStats({
        users: userRows.length,
        tickets: userRows.reduce((sum, user) => sum + user.ticketsCount, 0),
      })
    } catch (err) {
      console.error("Error loading raffle users:", err)
      setError("Failed to load raffle users.")
      setUsers([])
      setStats({ users: 0, tickets: 0 })
    } finally {
      setLoading(false)
    }
  }, [raffleId, raffle])

  useEffect(() => {
    fetchUsers()
  }, [fetchUsers])

  const handleOpenTickets = useCallback((user: RaffleUserRow) => {
    const tickets = [...(user.tickets || [])].sort(
      (a, b) => getExpirationMillis(a.expirationDate) - getExpirationMillis(b.expirationDate),
    )

    PopupService.setPopup(
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        <Typography variant="h6">{user.name || "Unknown user"}</Typography>
        {user.email ? <Typography variant="body2">{user.email}</Typography> : null}
        {user.phoneNumber ? <Typography variant="body2">{user.phoneNumber}</Typography> : null}
        <Typography variant="body2">{`Tickets: ${user.ticketsCount}`}</Typography>
        <Box sx={{ display: "grid", gap: 1, maxHeight: 360, overflowY: "auto" }}>
          {tickets.map((ticket) => (
            <Box
              key={ticket.id || ticket.code}
              sx={{ display: "flex", alignItems: "center", gap: 2 }}
            >
              <Typography variant="body2">{ticket.code || ticket.id}</Typography>
              <Typography variant="caption" color="text.secondary">
                {formatExpirationDate(ticket.expirationDate)}
              </Typography>
              {ticket.isWinner ? (
                <Typography variant="caption" color="success.main">
                  Winner
                </Typography>
              ) : null}
            </Box>
          ))}
        </Box>
      </Box>,
      "User Tickets",
    )
  }, [])

  const handleOpenUser = useCallback(
    (userId: string) => {
      navigate(`/users/${userId}`)
    },
    [navigate],
  )

  const columns = useMemo(
    () => [
      { name: "name", label: "Name" },
      { name: "email", label: "Email" },
      { name: "phoneNumber", label: "Phone" },
      { name: "ticketsCount", label: "Tickets" },
      {
        name: "actions",
        label: "Actions",
        options: {
          filter: false,
          sort: false,
          customBodyRenderLite: (dataIndex: number) => (
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                size="small"
                variant="outlined"
                onClick={(event) => {
                  event.stopPropagation()
                  handleOpenTickets(users[dataIndex])
                }}
              >
                Tickets
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={(event) => {
                  event.stopPropagation()
                  handleOpenUser(users[dataIndex].id)
                }}
              >
                User
              </Button>
            </Box>
          ),
        },
      },
    ],
    [handleOpenTickets, handleOpenUser, users],
  )

  const options = useMemo(
    () => ({
      selectableRows: "none" as const,
      rowsPerPage: 25,
      rowsPerPageOptions: [10, 25, 50, 100],
      responsive: "simple" as const,
    }),
    [],
  )

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 2,
          mb: 2,
        }}
      >
        <Typography variant="h6">Users</Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
          <Typography variant="body2">{`Users: ${stats.users}`}</Typography>
          <Typography variant="body2">{`Tickets: ${stats.tickets}`}</Typography>
          <Button size="small" variant="outlined" onClick={fetchUsers} disabled={loading}>
            Refresh
          </Button>
        </Box>
      </Box>

      {error ? (
        <Typography color="error" variant="body2" sx={{ mb: 2 }}>
          {error}
        </Typography>
      ) : null}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      ) : users.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No users found for this raffle.
        </Typography>
      ) : (
        <MUIDataTable title="Raffle Users" data={users} columns={columns} options={options} />
      )}
    </Box>
  )
}

export default RaffleUsers
